/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useLoanContext } from '../../contexts/LoanContext';
import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { AxiosError } from 'axios';

interface keywordSets {
  include: string;
  exclude: string;
}

interface DocumentsReq {
  keywordSets?: Array<keywordSets>;
  conditions?: Array<string>;
  documentTypes?: { include: Array<string>; exclude: Array<string> };
  startDate?: string;
  subcategory?: number;
  latestOnly?: boolean;
}

const useDocumentsRecognize = () => {
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const { get } = useAbortController();
  const [documentsData, setDocumentsData] = useState(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const handleError = useCallback((res: Error | AxiosError | any) => {
    const err = getErrorFromEndpoint(res);
    if (isStrEmptyOrNull(err)) return '';
    console.log('Error in documentsRecognizeAPI in useDocumentsRecognize hook: ' + err);
    setErrors(err);
    return err;
  }, []);

  const documentsRecognizeAPI = useCallback(
    async (params: DocumentsReq, docType: string): Promise<DocumentRecognizeType[] | null> => {
      if (!token || !params) return;
      var urlPath = process.env.REACT_APP_DOCUMENTS_RECOGNIZE.replace('?', loanNumber) + docType;
      let request = { url: urlPath, data: params, method: 'post' };
      var headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();

      const res = await axiosCall(request, config, headers)
        .then((res) => {
          if (!isObjEmptyOrNull(res)) {
            var err = getErrorFromEndpoint(res);
            if (isStrEmptyOrNull(err)) {
              setDocumentsData(res);
              return res;
            } else {
              setErrors(err);
              return null;
            }
          }
        })
        .catch((e: AxiosError | Error | any) => {
          handleError(e);
          return null;
        });
      return res;
    },
    [get, token, loanNumber, handleError],
  );

  const getDocumentInfo = useCallback(
    async (params: DocumentsReq, docType: string): Promise<DocumentRecognizeType[] | null> => {
      setLoading(true);
      setDocumentsData(null);
      if (!isStrEmptyOrNull(loanNumber) && !isObjEmptyOrNull(params)) {
        const res = await documentsRecognizeAPI(params, docType).finally(() => {
          setLoading(false);
        });
        return res;
      }
      return null;
    },
    [documentsRecognizeAPI, loanNumber],
  );

  return { documentsData, loading, errors, getDocumentInfo };
};

export default useDocumentsRecognize;
